import { useMemo, useState } from "react";
import { useListProjectFiles, useReadProjectFile } from "@vorloop/api-client-react";
import { ExpoPreviewPanel } from "./ExpoPreviewPanel";
import { FileTree } from "../files/FileTree";
import { IconSearch, IconX } from "../layout/Icons";

type RightTab = "preview" | "library" | "packages";

export function RightPanel({
  projectId,
  selectedPath,
  onSelectPath,
  onClose,
}: {
  projectId: string;
  selectedPath?: string | null;
  onSelectPath?: (path: string) => void;
  onClose?: () => void;
}) {
  const [tab, setTab] = useState<RightTab>("preview");

  return (
    <aside
      style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg-panel)",
        borderLeft: "1px solid var(--border-default)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom: "1px solid var(--border-default)",
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            onClick={() => setTab("preview")}
            style={panelTab(tab === "preview")}
          >
            <PreviewGlyph active={tab === "preview"} /> Preview on your phone
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setTab(tab === "library" ? "preview" : "library")}
          aria-label="Library"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: tab === "library" ? "var(--bg-active)" : "transparent",
            border: "1px solid var(--border-default)",
            color: "var(--text-secondary)",
            borderRadius: 6,
            padding: "3px 8px",
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          <FileTreeGlyph /> File tree
        </button>
        <button
          type="button"
          onClick={() => setTab(tab === "packages" ? "preview" : "packages")}
          aria-label="Packages"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: tab === "packages" ? "var(--bg-active)" : "transparent",
            border: "1px solid var(--border-default)",
            color: "var(--text-secondary)",
            borderRadius: 6,
            padding: "3px 8px",
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          📦 Packages
        </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-tertiary)",
              cursor: "pointer",
              padding: 4,
              display: "inline-flex",
            }}
          >
            <IconX />
          </button>
        )}
      </header>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: tab === "preview" || tab === "packages" ? 16 : 0 }}>
        {tab === "preview" ? (
          <ExpoPreviewPanel projectId={projectId} />
        ) : tab === "packages" ? (
          <PackagesView projectId={projectId} />
        ) : (
          <LibraryView
            projectId={projectId}
            selectedPath={selectedPath ?? null}
            onSelect={onSelectPath ?? (() => {})}
          />
        )}
      </div>
    </aside>
  );
}

function PackagesView({ projectId }: { projectId: string }) {
  const { data, isLoading, error } = useReadProjectFile(projectId, "package.json");
  const parsed = useMemo(() => {
    if (!data?.content) return null;
    try {
      const p = JSON.parse(data.content) as {
        name?: string;
        version?: string;
        dependencies?: Record<string, string>;
        devDependencies?: Record<string, string>;
      };
      return {
        name: p.name ?? null,
        version: p.version ?? null,
        deps: Object.entries(p.dependencies ?? {}),
        devDeps: Object.entries(p.devDependencies ?? {}),
      };
    } catch {
      return null;
    }
  }, [data?.content]);

  if (isLoading) {
    return <PackagesEmpty>Loading package.json…</PackagesEmpty>;
  }
  if (error) {
    return <PackagesEmpty error>{error.message}</PackagesEmpty>;
  }
  if (!parsed) {
    return <PackagesEmpty>No package.json yet — ask the agent to scaffold one.</PackagesEmpty>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, fontSize: 12 }}>
      <header>
        <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: 13 }}>
          {parsed.name ?? "(unnamed)"}
        </div>
        <div style={{ color: "var(--text-tertiary)", fontSize: 11, marginTop: 2 }}>
          {parsed.version ?? "no version"}
        </div>
      </header>

      <PackageList
        title="Dependencies"
        items={parsed.deps}
        empty="No runtime dependencies yet."
      />

      <PackageList
        title="Dev dependencies"
        items={parsed.devDeps}
        empty="No dev dependencies yet."
      />

      <p style={{ color: "var(--text-tertiary)", fontSize: 11, margin: 0, lineHeight: 1.5 }}>
        Read-only view. The agent's <code>add_dependency</code> tool edits this file when it needs a
        new package. Lockfile + version pinning land when the live API ships sandbox installs.
      </p>
    </div>
  );
}

function PackageList({
  title,
  items,
  empty,
}: {
  title: string;
  items: ReadonlyArray<[string, string]>;
  empty: string;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--text-tertiary)",
        }}
      >
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div style={{ color: "var(--text-tertiary)", fontSize: 11, padding: "8px 10px" }}>{empty}</div>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map(([name, version]) => (
            <li
              key={name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 8px",
                background: "var(--bg-panel-2)",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontFamily: "var(--font-mono)",
                  fontSize: 11.5,
                  color: "var(--text-primary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                }}
              >
                {version}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function PackagesEmpty({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        padding: 16,
        color: error ? "var(--accent-red)" : "var(--text-tertiary)",
        fontSize: 12,
      }}
    >
      {children}
    </div>
  );
}

const IMAGE_EXTS = new Set([".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".ico"]);
const APP_ENTRY_PATHS = new Set(["app.json", "app.config.ts", "app.config.js", "expo.json"]);

function LibraryView({
  projectId,
  selectedPath,
  onSelect,
}: {
  projectId: string;
  selectedPath: string | null;
  onSelect: (path: string) => void;
}) {
  const files = useListProjectFiles(projectId);
  const [query, setQuery] = useState("");
  const items = files.data?.items ?? [];

  const apps = useMemo(
    () =>
      items
        .filter((f) => APP_ENTRY_PATHS.has(f.path.toLowerCase()))
        .map((f) => ({ name: f.path, kind: "App config" })),
    [items],
  );
  const images = useMemo(
    () =>
      items
        .filter((f) => {
          const lower = f.path.toLowerCase();
          const dot = lower.lastIndexOf(".");
          if (dot < 0) return false;
          return IMAGE_EXTS.has(lower.slice(dot));
        })
        .map((f) => ({ name: f.path, kind: "Image" })),
    [items],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 10px",
          borderBottom: "1px solid var(--border-default)",
          color: "var(--text-tertiary)",
        }}
      >
        <IconSearch size={14} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search files"
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--text-secondary)",
            fontSize: 12,
            fontFamily: "var(--font-sans)",
          }}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: 8 }}>
        <LibrarySection label="Apps" items={apps} emoji="📱" onSelect={onSelect} />
        <LibrarySection label="Images" items={images} emoji="🖼" onSelect={onSelect} />
      </div>
      <div
        style={{
          padding: "6px 12px",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--text-tertiary)",
          borderTop: "1px solid var(--border-default)",
        }}
      >
        Files
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <FileTree projectId={projectId} selectedPath={selectedPath} onSelect={onSelect} />
      </div>
    </div>
  );
}

function LibrarySection({
  label,
  items,
  emoji,
  onSelect,
}: {
  label: string;
  items: ReadonlyArray<{ name: string; kind: string }>;
  emoji: string;
  onSelect?: (path: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div
        style={{
          padding: "4px 6px",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--text-tertiary)",
        }}
      >
        {label} ({items.length})
      </div>
      {items.length === 0 ? (
        <div style={{ padding: "4px 6px", color: "var(--text-tertiary)", fontSize: 11 }}>None</div>
      ) : (
        items.map((it) => (
          <button
            key={it.name}
            type="button"
            onClick={() => onSelect?.(it.name)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px",
              borderRadius: 6,
              fontSize: 12,
              color: "var(--text-primary)",
              cursor: onSelect ? "pointer" : "default",
              background: "transparent",
              border: "none",
              width: "100%",
              textAlign: "left",
            }}
          >
            <span aria-hidden style={{ fontSize: 14 }}>{emoji}</span>
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "var(--font-mono)", fontSize: 11.5 }}>
              {it.name}
            </span>
            <span style={{ color: "var(--text-tertiary)", fontSize: 10 }}>{it.kind}</span>
          </button>
        ))
      )}
    </div>
  );
}

function panelTab(active: boolean): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    border: "none",
    color: active ? "var(--text-primary)" : "var(--text-secondary)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    padding: "2px 4px",
  };
}

function PreviewGlyph({ active }: { active: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="6" y="2" width="12" height="20" rx="2" fill={active ? "currentColor" : "none"} opacity={active ? 0.1 : 1} />
      <rect x="6" y="2" width="12" height="20" rx="2" />
      <path d="M11 19h2" />
    </svg>
  );
}

function FileTreeGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 5h6l2 2h8v12H4z" />
    </svg>
  );
}
